/**
 * Checks if the values in the array have a trend. Assumes that the first value in the array is the most recent.
 * A trend is defined as at least two consecutive changes to values in the array in the same direction (positive, or negative).
 *
 * Because the most recent value is the first value in the array - the trend value is somewhat counter intuitive,
 * since for an array of [1, 2, 3] - the trend is actually negative, since the most recent value is 1, and the previous value is 2, etc.
 */
export function getAPYTrend(
  values: number[]
): { direction: 'positive' | 'negative'; count: number } | { direction: 'none' } {
  // map the differences between consecutive values to their signs
  const signs = values.slice(1).map((value, i) => Math.sign(value - values[i]));

  // not enough to determine a trend
  if (signs.length < 2) return { direction: 'none' };

  if (signs[0] === 0) return { direction: 'none' }

  const { count, trendSign } = signs.slice(1).reduce(
    ({ count, trendSign, trendStopped }, sign, index, all) => {
      if (trendStopped) return { count, trendSign, trendStopped };

      if (sign !== trendSign && sign !== 0) {
        return { count, trendSign, trendStopped: true };
      }

      return { count: index === 0 ? count + 2 : count + 1, trendSign, trendStopped };
    },
    { count: 0, trendSign: signs[0], trendStopped: false }
  );

  return count === 0 ? { direction: 'none' } : { direction: trendSign > 0 ? 'negative' : 'positive', count };
}
